import type { FileStorageItem } from '../api/fileStorageApi';

export type FileSortKey = 'name' | 'updatedAt' | 'length';
export type SortDirection = 'asc' | 'desc';

type DownloadPayload = {
  Content?: string;
  content?: string;
  Name?: string;
  name?: string;
  MimeType?: string;
  mimeType?: string;
};

export const formatFileSize = (
  item: FileStorageItem,
  options?: { emptyFileValue?: string }
): string => {
  if (item.isFolder) {
    const filesCount = item.length || 0;
    return `${filesCount} file${filesCount === 1 ? '' : 's'}`;
  }
  if (!item.length) return options?.emptyFileValue ?? '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = item.length;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

export const sortFileItems = (
  items: FileStorageItem[],
  sortKey: FileSortKey,
  sortDirection: SortDirection
): FileStorageItem[] => {
  const sorted = [...items];
  sorted.sort((a, b) => {
    if (sortKey === 'name') {
      return sortDirection === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    }

    if (sortKey === 'updatedAt') {
      const aDate = Date.parse(a.updatedAt || '') || 0;
      const bDate = Date.parse(b.updatedAt || '') || 0;
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    }

    return sortDirection === 'asc' ? a.length - b.length : b.length - a.length;
  });

  return sorted;
};

/** @returns true if a download was triggered, false if payload was missing or base64 decode failed */
export const downloadFromResponse = (
  response: DownloadPayload,
  fallbackFileName: string,
  fallbackMimeType = 'application/octet-stream'
): boolean => {
  const raw = response.Content ?? response.content;
  const content = typeof raw === 'string' ? raw.trim() : '';
  if (!content) return false;

  let bytes: Uint8Array;
  try {
    const binary = window.atob(content);
    bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
  } catch {
    return false;
  }

  const fileName = response.Name ?? response.name ?? fallbackFileName;
  const mimeType = response.MimeType ?? response.mimeType ?? fallbackMimeType;
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: mimeType });
  const href = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(href);
  return true;
};
